/**
 * #1310 PR-O — Stitch proposal review modal.
 *
 * Listed from `GET /:id/stitches/proposals` (added in #1289 / #1280
 * PR 9). Each row shows the proposal's rationale, AI reasoning
 * (collapsible), and confidence; two buttons confirm or reject.
 *
 * Confirm: `POST /:id/stitches/proposals/confirm` — the proposal
 * graduates from the pending bucket into `stitches.url.ai` so the
 * classifier picks it up on the next graph build.
 *
 * Reject: `POST /:id/stitches/proposals/reject` — the proposal is
 * removed; no analyzer state change.
 *
 * Mount via `<StitchProposalReview projectId={...} onClose={...} />`.
 * PR-P wires it to the catalog row so confirming reflows the row's
 * classification badge.
 */
import { useEffect, useState } from 'react';
import { apiFetch } from '../../services/api';

interface Proposal {
  callSite: {
    filePath: string;
    line: number;
    method: string;
    urlPattern: string;
    enclosingSymbol?: string;
  };
  route: {
    method: string;
    pathPattern: string;
    filePath?: string;
  };
  confidence?: number;
  rationale?: string;
  reasoning?: string;
  callSiteKey?: string;
}

interface StitchProposalReviewProps {
  projectId: string;
  onClose: () => void;
  /** Fires after a confirm/reject so parent can refetch counts. */
  onChange?: () => void;
}

export function StitchProposalReview({
  projectId,
  onClose,
  onChange,
}: StitchProposalReviewProps) {
  const [proposals, setProposals] = useState<Proposal[] | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    apiFetch(`/projects/${projectId}/stitches/proposals`, {
      credentials: 'include',
    })
      .then(r => r.json())
      .then((body: { proposals?: Proposal[] }) => {
        if (cancelled) return;
        setProposals(body.proposals ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        setProposals([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  function keyOf(p: Proposal): string {
    return (
      p.callSiteKey ??
      `${p.callSite.filePath}:${p.callSite.line}:${p.callSite.method}:${p.callSite.urlPattern}`
    );
  }

  async function act(p: Proposal, action: 'confirm' | 'reject') {
    const k = keyOf(p);
    setBusyKey(k);
    try {
      const r = await fetch(
        `/api/projects/${projectId}/stitches/proposals/${action}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ callSiteKey: k }),
        },
      );
      if (!r.ok) throw new Error(`${r.status}`);
      setProposals(prev => (prev ? prev.filter(q => keyOf(q) !== k) : prev));
      onChange?.();
    } finally {
      setBusyKey(null);
    }
  }

  function toggleExpand(k: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-[min(720px,calc(100vw-2rem))] max-h-[80vh] overflow-hidden flex flex-col bg-white dark:bg-slate-900 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-label="Stitch proposals review"
      >
        <header className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Review stitch proposals
            {proposals && (
              <span className="ml-2 text-xs font-normal text-slate-500">
                ({proposals.length} pending)
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {proposals === null && (
            <div className="text-sm text-slate-500 text-center py-8">
              Loading proposals…
            </div>
          )}
          {proposals && proposals.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-8">
              No pending proposals. Click "suggest stitches" on a needs-review
              flow to generate some.
            </div>
          )}
          {proposals?.map(p => {
            const k = keyOf(p);
            const isBusy = busyKey === k;
            const isExpanded = expanded.has(k);
            return (
              <div
                key={k}
                className="border border-slate-200 dark:border-slate-700 rounded-md p-3 bg-slate-50 dark:bg-slate-800/50"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono text-slate-600 dark:text-slate-300">
                      <span className="font-semibold text-amber-700 dark:text-amber-400">
                        {p.callSite.method}
                      </span>{' '}
                      <span>{p.callSite.urlPattern}</span>
                      <span className="text-slate-400"> → </span>
                      <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                        {p.route.method}
                      </span>{' '}
                      <span>{p.route.pathPattern}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1 truncate">
                      {p.callSite.filePath}:{p.callSite.line}
                      {p.callSite.enclosingSymbol &&
                        ` · ${p.callSite.enclosingSymbol}`}
                    </div>
                    {p.rationale && (
                      <div className="text-xs text-slate-700 dark:text-slate-300 mt-2">
                        {p.rationale}
                      </div>
                    )}
                    {p.reasoning && (
                      <div className="mt-2">
                        <button
                          onClick={() => toggleExpand(k)}
                          className="text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                          {isExpanded ? 'Hide' : 'Show'} AI reasoning
                        </button>
                        {isExpanded && (
                          <pre className="mt-1 text-[11px] text-slate-600 dark:text-slate-300 whitespace-pre-wrap bg-white dark:bg-slate-900 p-2 rounded border border-slate-200 dark:border-slate-700">
                            {p.reasoning}
                          </pre>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    {typeof p.confidence === 'number' && (
                      <span className="text-[10px] text-slate-500">
                        {Math.round(p.confidence * 100)}% conf.
                      </span>
                    )}
                    <button
                      onClick={() => act(p, 'confirm')}
                      disabled={isBusy}
                      className="px-2 py-0.5 text-xs font-medium rounded bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 disabled:opacity-50"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => act(p, 'reject')}
                      disabled={isBusy}
                      className="px-2 py-0.5 text-xs font-medium rounded bg-rose-50 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/60 disabled:opacity-50"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
